import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import sortBy from 'lodash.sortby';
import ChannelsCreateModal from '@features/ChannelsCreateModal';
import ContactFieldsSettings from '@features/ContactFieldsSettings';
import { Input } from '@components/Input';
import { Switch } from '@components/Switch';
import { MultiSelect } from '@components/MultiSelect';
import { Typography } from '@components/Typography';
import { useTranslation, useChannels } from '@hooks';
import { CHANNEL, STATUS } from '@constants/channels';

const GeneralFields = ({ isView, selectedAgentRef, isEdit, canWrite }) => {
    const [createModal, setCreateModal] = useState({
        open: false,
        step: 1,
        type: CHANNEL.SMS,
        status: STATUS.PENDING,
        qrCode: '',
        liveChatPublicId: null,
    });
    const [selectedChannel, setSelectedChannel] = useState();

    const { t } = useTranslation();
    const { control, setValue, getValues } = useFormContext();
    const { getChannelOptions } = useChannels();
    const channels = useSelector(state => state.channels.data);

    const channelsOptions = useMemo(() => {
        // INFO: withDisconnected to show disconnected channels in options when editing or viewing
        return sortBy(
            getChannelOptions(
                {
                    in: true,
                    withDisconnected: isEdit || isView,
                    withDisabledAgents: true,
                    currentAgent: selectedAgentRef.current?.id,
                },
                channels.map(channel => channel.id),
            ),
            ['name'],
        );
    }, [channels, isEdit, isView]);

    return (
        <>
            <Controller
                control={control}
                name="name"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('common.name')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                        disabled={isView}
                    />
                )}
            />
            <Controller
                control={control}
                name="channels"
                render={({ field, fieldState: { error }, ref }) => (
                    <MultiSelect
                        {...field}
                        ref={ref}
                        onChange={value => {
                            setValue('channels', value, { shouldValidate: true, shouldDirty: true });
                        }}
                        options={channelsOptions}
                        label={t('filters.channels')}
                        fullWidth
                        countable
                        nameBased
                        required
                        disabled={isView}
                        error={error}
                        creatable={canWrite}
                        creatableText={t('SettingsChannels.createNewChannel')}
                        creatableClick={() => setCreateModal(prev => ({ ...prev, open: true }))}
                    />
                )}
            />
            <Controller
                control={control}
                name="status"
                render={({ field, ref }) => (
                    <Switch
                        {...field}
                        ref={ref}
                        checked={getValues('status')}
                        handleChange={event => {
                            setValue('status', event.target.checked, { shouldDirty: true });
                        }}
                        label={t('Agents.activateAgent')}
                        disabled={isView}
                        sx={{ marginTop: '-16px' }}
                    />
                )}
            />
            <div>
                <Typography variant="subtitle1" sx={{ color: 'var(--text-primary)' }}>
                    {t('FeedbackSurveys.contactDetails')}
                </Typography>
                <Typography
                    variant="caption"
                    sx={{ color: 'var(--text-secondary)', display: 'block', marginTop: '4px' }}
                >
                    {t('Agents.contactDetailsDescription')}
                </Typography>
                <ContactFieldsSettings disabled={isView} />
            </div>
            {createModal.open ? (
                <ChannelsCreateModal
                    modal={createModal}
                    setModal={setCreateModal}
                    channel={selectedChannel}
                    setChannel={setSelectedChannel}
                />
            ) : null}
        </>
    );
};

export default GeneralFields;
